import { styled } from '@mui/material';
import {
  DataGridPro as DataGrid,
  GridColumnMenu,
  type DataGridProProps,
  type GridColumnMenuProps,
  type GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { ROW_HEIGHT, useMenuItemActions } from '~/components/data-grid';

const CustomColumnMenu = (props: GridColumnMenuProps) => (
  <GridColumnMenu
    {...props}
    slots={{
      columnMenuFilterItem: null,
      columnMenuPinningItem: null,
    }}
  />
);

export const DataSourceActions = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  paddingBottom: theme.spacing(2),
}));

export const MetaItem = styled('div')(({ theme }) => ({
  display: 'inline-block',
  padding: theme.spacing(0, 3),
  borderRight: `1px solid ${theme.palette.borders.light}`,
  '& img': {
    height: '25px',
  },
  '&.first': {
    paddingLeft: '0px',
  },
  '&.last': {
    borderWidth: '0px',
  },
}));

const StyledDataGrid = styled(DataGrid)<DataGridProProps<GridValidRowModel>>(({ theme }) => ({
  '--DataGrid-overlayHeight': '225px',
  '--DataGrid-rowBorderColor': 'transparent',
  border: 0,
  fontSize: '14px',
  '& .MuiDataGrid-main': {
    border: `1px solid ${theme.palette.borders.light}`,
    borderRadius: '4px',
  },
  '& .MuiDataGrid-columnHeader': {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
    '& .MuiDataGrid-columnSeparator--resizable': {
      display: 'flex',
      color: 'rgba(0,0,0,0.25)',
      '&:hover': {
        color: 'rgba(0,0,0,0.5)',
      },
    },
  },
  '& .MuiDataGrid-actionsCell': {
    gridGap: 0,
  },
  '& .MuiDataGrid-row': {
    borderRadius: '0',
    boxShadow: 'none',
    background: 'white',
    borderBottom: `1px solid ${theme.palette.borders.light}`,
    '&:hover': {
      background: theme.palette.table.hoverBackgroundColor,
      boxShadow: 'none',
    },
  },
  '& .MuiDataGrid-cell': {
    border: 0,
  },
  '& .MuiDataGrid-footerContainer': {
    minHeight: 52,
    maxHeight: 52,
  },
  // Spreadsheet-specific: row number column styling
  '& [data-field="id"]': {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.secondary,
    borderRight: `1px solid ${theme.palette.borders.light}`,
  },
  // Spreadsheet-specific: actions column styling
  '& [data-field="actions"]': {
    borderLeft: `1px solid ${theme.palette.borders.light}`,
    '& .MuiDataGrid-actionsCell': {
      justifyContent: 'center',
    },
  },
  // Spreadsheet-specific: disabled row styling
  '& .disabled': {
    backgroundColor: theme.palette.background.default,
  },
}));

export const SpreadsheetyDataGrid = ({ columns, slotProps, ...props }: DataGridProProps) => (
  <StyledDataGrid
    columns={useMenuItemActions(columns)}
    getRowHeight={() => ROW_HEIGHT}
    slots={{ columnMenu: CustomColumnMenu, ...props.slots }}
    slotProps={{
      loadingOverlay: {
        variant: 'skeleton',
      },
      ...slotProps,
    }}
    {...props}
  />
);
